<template>
    <v-menu
        v-model="showPopup"
        :close-on-content-click="false"
        lazy
        transition="scale-transition"
        offset-y
        max-width="250"
    >
        <template v-slot:activator="{ on }">
            <v-text-field
                v-model="dateTime"
                :label="label || ''"
                :placeholder="placeholder || ''"
                persistent-hint
                :prepend-inner-icon="iconStart || ''"
                :append-icon="iconEnd || ''"
                :disabled="disabled"
                readonly
                v-on="on"
                :rules="rules ? rules : []"
                :class="cssClass || ''"
                :clearable="clearable"
            ></v-text-field>
        </template>
        <div>
            <div v-if="!emitDateOnly && !emitTimeOnly"
                 class="datetime-steps flexbox flex-justify-content-space-between text-xs-center">
                <span @click="step = 'select-date'" :class="{active: step === 'select-date'}" class="bg-primary">
                    <v-icon dark>mdi-calendar-month-outline</v-icon>
                </span>
                <span @click="step = 'select-time'" :class="{active: step === 'select-time'}" class="bg-primary">
                    <v-icon dark>mdi-clock-outline</v-icon>
                </span>
            </div>
            <v-layout row>
                <v-date-picker
                    v-if="!emitTimeOnly && step === 'select-date'"
                    class="elevation-0"
                    v-model="date"
                    no-title
                    :min="minDate"
                    @input="checkMinTime(); select()"
                    width="250"
                ></v-date-picker>
                <v-time-picker
                    v-if="!emitDateOnly && step === 'select-time'"
                    class="elevation-0"
                    v-model="time"
                    :min="minTimeTMP || minTime"
                    @input="select()"
                    width="250"
                ></v-time-picker>
            </v-layout>
        </div>
    </v-menu>
</template>

<script>
import moment from 'moment'

export default {
    name: 'DateTimePicker',
    props: {
        value: String,
        label: String,
        placeholder: String,
        disabled: Boolean,
        rules: Array,
        min: String,
        cssClass: String,
        iconStart: String,
        iconEnd: String,
        setDefaultCurrentTime: Boolean,
        emitDateOnly: Boolean,
        emitTimeOnly: Boolean,
        clearable: Boolean
    },
    data() {
        let today = this.$moment().format('YYYY-MM-DD')
        return {
            today: today,
            showPopup: false,
            date: null,
            time: null,
            dateTime: null,
            step: 'select-date',
            minTimeTMP: false
        }
    },
    mounted() {
        if (this.emitTimeOnly) {
            this.step = 'select-time'
        }
        if (this.value) {
            this.initValue(this.value)
        }
    },
    computed: {
        minDate() {
            return this.min ? moment(this.min).format(this.$const.dateFormat) : moment().format(this.$const.dateFormat)
        },
        minTime() {
            if (this.emitTimeOnly) {
                return this.min || ''
            } else {
                return this.min && this.min !== '' && this.min !== null ? moment(this.min).format(this.$const.timeFormat) : moment().format(this.$const.timeFormat)
            }
        }
    },
    methods: {
        initValue(value) {
            if (this.emitDateOnly && !this.emitTimeOnly) {
                value = moment(value).format(this.$const.dateFormat)
                this.dateTime = value
                this.date = value
            } else if (!this.emitDateOnly && this.emitTimeOnly) {
                value = moment(value, this.$const.timeFormat).format(this.$const.timeFormat)
                this.dateTime = value
                this.time = value
            } else {
                this.dateTime = value
                this.date = moment(value).format(this.$const.dateFormat)
                this.time = moment(value, this.$const.timeFormat).format(this.$const.timeFormat)
            }
        },
        checkMinTime() {
            let startDate = false
            if (this.min && this.min !== '' && this.min !== null) {
                startDate = moment(this.min, this.$const.dateFormat)
            } else {
                startDate = moment(new Date, this.$const.dateFormat)
            }
            let endDate = moment(this.date, this.$const.dateFormat)
            if (endDate.isAfter(startDate)) {
                this.minTimeTMP = '00:00'
            } else {
                this.minTimeTMP = false
            }
        },
        select() {
            if (this.emitDateOnly && !this.emitTimeOnly) {
                if (this.date !== '' && this.date !== null) {
                    this.$emit('input', this.date)
                    this.showPopup = false
                }
            } else if (!this.emitDateOnly && this.emitTimeOnly) {
                if (this.time !== '' && this.time !== null) {
                    this.$emit('input', this.time)
                    this.showPopup = false
                }
            } else {
                if (this.date !== '' && this.date !== null && this.time !== '' && this.time !== null) {
                    this.$emit('input', this.date + ' ' + this.time)
                    this.showPopup = false
                }
            }
        },
        hasData(obj) {
            return obj !== '' && obj !== null ? obj : false
        }
    },
    watch: {
        value(value) {
            if (value !== '' && value !== null) {
                if (this.emitTimeOnly) {
                    let val = moment(value, this.$const.timeFormat).format(this.$const.timeFormat)
                    this.time = val
                    this.dateTime = val
                } else {
                    this.date = value
                    this.dateTime = value
                }
            }
        },
        min(value) {
            this.$nextTick().then(() => {
                if (this.emitDateOnly && !this.emitTimeOnly) {
                    let data = this.hasData(this.date) || this.hasData(this.dateTime)
                    if (data) {
                        let start = moment(value, this.$const.dateFormat)
                        let end = moment(data, this.$const.dateFormat)
                        if (end.isBefore(start)) {
                            this.date = ''
                            this.dateTime = ''
                            this.$emit('input', '')
                        }
                    }
                } else if (!this.emitDateOnly && this.emitTimeOnly) {
                    let data = this.hasData(this.time) || this.hasData(this.dateTime)
                    if (data) {
                        let start = moment(value, this.$const.timeFormat)
                        let end = moment(data, this.$const.timeFormat)
                        if (end.isBefore(start)) {
                            this.time = ''
                            this.dateTime = ''
                            this.$emit('input', '')
                        }
                    }
                } else {
                    let dateData = this.hasData(this.date)
                    let timeData = this.hasData(this.time)
                    let dateTimeData = this.hasData(this.dateTime)
                    if ((!dateData || !timeData) && dateTimeData) {
                        dateData = moment(dateTimeData).format(this.$const.dateFormat)
                        timeData = moment(dateTimeData, this.$const.timeFormat).format(this.$const.timeFormat)
                    }
                    if (dateData && timeData) {
                        let invalid = false
                        let startDate = moment(value, this.$const.dateFormat)
                        let endDate = moment(dateData, this.$const.dateFormat)
                        let startTime = moment(value, this.$const.timeFormat)
                        let endTime = moment(timeData, this.$const.timeFormat)
                        if (endDate.isBefore(startDate)) {
                            invalid = true
                        } else if (endTime.isBefore(startTime)) {
                            invalid = true
                        }
                        if (invalid) {
                            this.date = ''
                            this.time = ''
                            this.dateTime = ''
                            this.$emit('input', '')
                        }
                    }
                }
            })
        },
        dateTime(val) {
            this.$emit('input', val)
        }
    }
}
</script>

<style scoped>
/deep/ .datetime-actions {
    display: block;
    padding: 10px 5px 5px 0;
    background: #fff;
}

/deep/ .v-time-picker-title__time .v-picker__title__btn,
/deep/ .v-time-picker-title__time span {
    font-size: 30px;
    height: 35px;
    padding: 0 4px;
}

/deep/ .v-time-picker-title__time .v-picker__title__btn {
    position: relative;
}

/deep/ .v-time-picker-title__time .v-picker__title__btn:before,
/deep/ .v-time-picker-title__time .v-picker__title__btn:after {
    border: solid transparent;
    content: " ";
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    border-width: 5px;
}

/deep/ .v-time-picker-title__time .v-picker__title__btn:before {
    bottom: 100%;
    left: 50%;
    border-color: rgba(255, 255, 255, 0);
    border-bottom-color: rgba(255, 255, 255, 0.6);
    margin-left: -5px;
    margin-bottom: 2px;
}

/deep/ .v-time-picker-title__time .v-picker__title__btn:after {
    top: 100%;
    left: 50%;
    border-color: rgba(255, 255, 255, 0);
    border-top-color: rgba(255, 255, 255, 0.6);
    margin-left: -5px;
    margin-top: 2px;
}

/deep/ .v-time-picker-title__ampm {
    margin: 0;
    line-height: 26px;
    flex-direction: row;
}

/deep/ .v-time-picker-title__ampm .v-picker__title__btn {
    margin: 0 0 0 20px;
    font-weight: bold;
}

/deep/ .v-time-picker-clock__container {
    padding-top: 16px;
}

.datetime-steps > span {
    width: 50%;
    padding: 10px 0;
    background: #04b6d4;
}

.datetime-steps > span.active {
    background: #0052CC;
}

.datetime-steps i {
    font-size: 20px;
}
</style>
